/**
 * IconWrapper component for marketing pages
 * Provides consistent icon container styling with gradient backgrounds
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FEATURE_COLORS, type FeatureColor } from '@/lib/design-tokens';

export interface IconWrapperProps {
  icon: LucideIcon;
  color?: FeatureColor;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'w-10 h-10',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-14 h-14',
} as const;

const iconSizeClasses = {
  sm: 'h-5 w-5',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-7 w-7',
} as const;

export function IconWrapper({
  icon: Icon,
  color = 'blue',
  size = 'md',
  className = '',
}: IconWrapperProps) {
  const colorConfig = FEATURE_COLORS[color];

  return (
    <div
      className={cn(
        sizeClasses[size],
        `bg-gradient-to-br ${colorConfig.from} ${colorConfig.to} rounded-xl flex items-center justify-center mb-4 shadow-lg ${colorConfig.shadow}`,
        className
      )}
    >
      <Icon className={cn(iconSizeClasses[size], 'text-white')} />
    </div>
  );
}
